/**
 * Rules governing when a purchase's phase (step) may be changed.
 *
 * These mirror `checkContractSigning` in app-project-single-purchase.jsx, which on every load
 * forces a purchase into Avtal (step 4) when a contract exists and back out when it doesn't.
 * Anything that changes a step has to respect the same rules, otherwise the change is silently
 * undone on the next load.
 */

export const AGREEMENT_STEP = '4';

type ContractLike = { lastStatus?: string };

export type PurchaseAreaLike = {
  step?: string | number,
  contracts?: ContractLike[],
  manualContract?: unknown[],
};

/**
 * A signed e-contract or an uploaded manual contract is what pins a purchase to Avtal.
 */
export function hasBindingContract(area: PurchaseAreaLike | null | undefined): boolean {
  if (!area) return false;
  const signed = area.contracts?.some(contract => contract?.lastStatus === 'closed') ?? false;
  return signed || (area.manualContract?.length ?? 0) > 0;
}

/**
 * Whether a purchase can be moved to `targetStep`.
 *
 * A contract pins a purchase to Avtal no matter which step it currently sits in, so the check is
 * on the target, not the current step: moving a contracted purchase anywhere other than Avtal is
 * futile because checkContractSigning drags it back on the next load.
 */
export function canChangeStepTo(
  area: PurchaseAreaLike | null | undefined,
  targetStep: string | number,
): boolean {
  if (String(targetStep) === AGREEMENT_STEP) return true;
  return !hasBindingContract(area);
}
